import React, { useMemo } from 'react';
import { Info, SquarePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AppCardSkeleton from './AppCardSkeleton';
import { useAppFavorites, type AppItem } from '@/hooks/app-library/use-app-favorites';
import { usePinMode } from '@/hooks/app-library/use-pin-mode';
import { usePinnedAppsWithDetails } from '@/hooks/app-library/use-user-pinned-apps';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { Separator } from '@/components/ui/separator';
import AppCard from './AppCard';
import { useAppLibrary } from './AppLibraryContext';
import { AppLibraryHeader } from './AppLibraryHeader';
import DashboardCard from '@/components/ui/DashboardCard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import PinnedAppsDnD from './PinnedAppsDnD';
import AppLibraryEmptyState from './AppLibraryEmptyState';
import { Button } from '@/components/ui/button';

interface AppLibraryContainerProps {
  onRequestApp?: (category?: string) => void;
}

const AppLibraryContainer: React.FC<AppLibraryContainerProps> = ({ onRequestApp }) => {
  const { selectedCategory, setSelectedCategory } = useAppLibrary();
  const { appItems, isLoading: appItemsLoading, hasError: appItemsError } = useAppFavorites();
  const { isPinModeActive, handlePositionSwap } = usePinMode();
  const { isAppPinned, getPinnedAppDetails, pinnedApps, appItems: pinnedAppItems } = usePinnedAppsWithDetails();
  const isMobile = useIsMobile();

  // Memoize filtered apps - now shows ALL apps (available + coming-soon)
  const filteredApps = useMemo(() => {
    if (!appItems) return [];
    
    return appItems.filter(app => {
      // Always hide pinned apps from the main grid since they're shown at the top
      if (isAppPinned(app.id)) {
        return false;
      }
      
      // Apply category filter
      if (selectedCategory === 'all') return true;
      if (selectedCategory === 'favorites') return app.is_favorited === true;
      return app.category === selectedCategory;
    });
  }, [appItems, selectedCategory, isAppPinned]);

  // Memoize the pinned apps section with DND functionality
  const pinnedAppsSection = useMemo(() => {
    const pinnedApp1 = getPinnedAppDetails(1);
    const pinnedApp2 = getPinnedAppDetails(2);
    const pinnedApp3 = getPinnedAppDetails(3);
    const pinnedApp4 = getPinnedAppDetails(4);
    
    return (
      <motion.div 
        className="mb-3 md:mb-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
      >
        <div className="bg-gradient-to-r from-purple-50/60 to-blue-50/60 dark:from-purple-950/20 dark:to-blue-950/20 rounded-xl border border-purple-200/50 dark:border-purple-800/30 p-4 md:p-6 mb-2 md:mb-3">
          <div className="flex flex-col md:flex-row md:items-baseline md:gap-4">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-400 dark:to-blue-400">
              Main Menu
            </h3>
            <p className="text-sm text-muted-foreground/80 mt-1 md:mt-0 font-medium">
              Organize your menu
            </p>
          </div>
        </div>
        
        <PinnedAppsDnD
          pinnedApps={[pinnedApp1, pinnedApp2, pinnedApp3, pinnedApp4]}
          onPositionSwap={handlePositionSwap}
        />
        
        <Separator className="mt-3 md:mt-6" />
      </motion.div>
    );
  }, [getPinnedAppDetails, handlePositionSwap]);

  // Memoize the app cards (now includes both available and coming-soon)
  const appCards = useMemo(() => {
    if (appItemsLoading) {
      return (
        <motion.div 
          className="grid grid-cols-1 2xl:grid-cols-2 gap-2 md:gap-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{ 
            perspective: '1000px',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden'
          }}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <motion.div
              key={`skeleton-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.2, 
                delay: index * 0.05,
                ease: [0.25, 0.1, 0.25, 1.0]
              }}
              className="rounded-lg"
            >
              <AppCardSkeleton />
            </motion.div>
          ))}
        </motion.div>
      );
    }

    if (appItemsError) {
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Failed to load apps. Please try again.</p>
        </div>
      );
    }
    
    if (filteredApps.length === 0) {
      return (
        <AppLibraryEmptyState
          isLoading={appItemsLoading}
          hasError={!!appItemsError}
          hasData={false}
          selectedCategory={selectedCategory}
          onRequestApp={onRequestApp}
        />
      );
    }

    return (
      <motion.div 
        className="grid grid-cols-1 2xl:grid-cols-2 gap-2 md:gap-8"
        layout
        style={{ 
          perspective: '1000px', // Helps with 3D rendering performance
          transform: 'translateZ(0)', // Hardware acceleration
          backfaceVisibility: 'hidden' // Prevent flickering
        }}
      >
        <AnimatePresence mode="popLayout">
          {filteredApps.map((app) => (
            <motion.div 
              key={app.id}
              layoutId={`app-${app.id}`}
              className="rounded-lg"
              initial={{ 
                opacity: 0, 
                scale: 0.9,
                y: 10
              }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                y: 0
              }}
              exit={{ 
                opacity: 0, 
                scale: 0.9,
                y: -10
              }}
              transition={{
                duration: 0.2,
                ease: [0.25, 0.1, 0.25, 1.0],
                layout: {
                  duration: 0.3,
                  ease: [0.25, 0.1, 0.25, 1.0]
                }
              }}
            >
              <AppCard 
                app={app} 
                isPinModeActive={isPinModeActive}
                isGreyedOut={app.status !== 'active'} // Grey out non-active apps
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    );
  }, [filteredApps, isMobile, appItemsLoading, appItemsError, selectedCategory, onRequestApp, isPinModeActive]);

  return (
    <DashboardCard variant="purple" className="p-3 md:p-6 md:pb-8">
      {/* Header with filter buttons */}
      <AppLibraryHeader 
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        isLoading={appItemsLoading}
        onRequestApp={onRequestApp}
      />
      
      {/* Pinned apps section - always visible since pin mode is always active */}
      {pinnedAppsSection}
      
      {/* Apps section - now shows ALL apps (available + coming-soon) */}
      <div className="bg-gradient-to-r from-green-50/60 to-emerald-50/60 dark:from-green-950/20 dark:to-emerald-950/20 rounded-xl border border-green-200/50 dark:border-green-800/30 p-4 md:p-6 mb-2 md:mb-3">
        <div className="flex flex-col md:flex-row md:items-baseline md:gap-4">
          <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400">
            Apps
          </h3>
          <p className="text-sm text-muted-foreground/80 mt-1 md:mt-0 font-medium">
            Available apps and coming soon
          </p>
        </div>
      </div>
      
      {/* Apps grid */}
      <div className={`w-full ${isMobile ? 'min-h-[300px]' : 'min-h-[400px]'}`}>
        {appCards}
      </div>
    </DashboardCard>
  );
};

export default AppLibraryContainer;
